import React, { useState, useEffect, useRef } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { Info, RefreshCw, Coins } from 'lucide-react';

const TokenInfo = () => {
  const { t } = useLanguage();
  const { web3, account, contract, contractAddress, refreshTick } = useWeb3();
  const [tokenInfo, setTokenInfo] = useState({
    name: '',
    symbol: '',
    decimals: 0,
    totalSupply: '0',
    balance: '0',
    owner: '',
    paused: false,
  });
  const [loading, setLoading] = useState(false);

  const loadTokenInfo = async () => {
    if (!contract || !web3) {
      console.log('Contrato o Web3 no disponible');
      return;
    }

    setLoading(true);
    try {
      const [name, symbol, decimals, totalSupply, owner, paused] = await Promise.all([
        contract.methods.name().call(),
        contract.methods.symbol().call(),
        contract.methods.decimals().call(),
        contract.methods.totalSupply().call(),
        contract.methods.owner().call(),
        contract.methods.paused().call(),
      ]);

      let balance = '0';
      if (account) {
        balance = await contract.methods.balanceOf(account).call();
      }

      setTokenInfo({
        name,
        symbol,
        decimals: Number(decimals),
        totalSupply: web3.utils.fromWei(totalSupply, 'ether'),
        balance: web3.utils.fromWei(balance, 'ether'),
        owner,
        paused,
      });
    } catch (error) {
      console.error('Error cargando información del token:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTokenInfo();
  }, [contract, account, web3]);

  // Refresco de saldo y supply en cada tick de polling (cada 10 s)
  // Usa refreshTick (siempre incrementa) en lugar de blockNumber (puede quedarse estático
  // si la red no produce bloques de forma continua)
  const initialLoad = useRef(true);
  useEffect(() => {
    if (initialLoad.current) { initialLoad.current = false; return; }
    if (!contract || !web3) return;
    const refreshDynamic = async () => {
      try {
        const [totalSupply, balance] = await Promise.all([
          contract.methods.totalSupply().call(),
          account ? contract.methods.balanceOf(account).call() : Promise.resolve('0'),
        ]);
        setTokenInfo(prev => ({
          ...prev,
          totalSupply: web3.utils.fromWei(totalSupply, 'ether'),
          balance: web3.utils.fromWei(balance, 'ether'),
        }));
      } catch (_) {}
    };
    refreshDynamic();
  }, [refreshTick]);

  if (!contractAddress || contractAddress === '') {
    return (
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Info className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('tokenInfo.title')}</h2>
        </div>
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>{t('tokenInfo.configRequired')}</strong> {t('tokenInfo.configMessage')}
          </p>
          <p className="text-xs text-yellow-700 mt-2">
            VITE_TOKEN_CONTRACT_ADDRESS=tu_direccion_del_contrato
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Info className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-gray-800">{t('tokenInfo.title')}</h2>
        </div>
        
        <button
          onClick={loadTokenInfo}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title={t('tokenInfo.refresh')}
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
          <span className="text-sm font-medium text-gray-600">{t('tokenInfo.name')}</span>
          <span className="text-lg font-bold text-gray-800">{tokenInfo.name || '-'}</span>
        </div>

        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
          <span className="text-sm font-medium text-gray-600">{t('tokenInfo.symbol')}</span>
          <span className="text-lg font-bold text-gray-800">{tokenInfo.symbol || '-'}</span>
        </div>

        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
          <span className="text-sm font-medium text-gray-600">{t('tokenInfo.totalSupply')}</span>
          <span className="text-lg font-bold text-gray-800">
            {parseFloat(tokenInfo.totalSupply).toLocaleString()} {tokenInfo.symbol}
          </span>
        </div>

        {account && (
          <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
            <div className="flex items-center space-x-2">
              <Coins className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-600">{t('tokenInfo.yourBalance')}</span>
            </div>
            <span className="text-xl font-bold text-green-600">
              {parseFloat(tokenInfo.balance).toLocaleString()} {tokenInfo.symbol}
            </span>
          </div>
        )}

        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-600">{t('tokenInfo.status')}</span>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
            tokenInfo.paused 
              ? 'bg-red-100 text-red-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {tokenInfo.paused ? t('tokenInfo.paused') : t('tokenInfo.active')}
          </span>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">{t('tokenInfo.contractAddress')}</p>
          <p className="text-xs font-mono text-gray-800 break-all">{contractAddress}</p>
        </div>
      </div>
    </div>
  );
};

export default TokenInfo;
